import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent, type RefObject } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { Button, EmptyState, ErrorText, Input, Loading, Textarea } from '../components/ui';
import { errorMessage } from '../lib/api';
import { timeAgo, timecode } from '../lib/format';
import { viewerName } from '../lib/storage';
import type { Comment } from '../lib/types';
import { qk } from '../queries/keys';
import { fetchComments, postComment } from '../queries/share';

/**
 * Timecoded comments.
 *
 * The timecode is the point: "2:14 — can we change this?" is a review note,
 * where "can we change this?" is a riddle. Clicking one seeks the player, so
 * feedback and the frame it is about stay attached.
 */

function CommentBody({
  comment,
  onSeek,
}: {
  comment: Comment;
  onSeek: (seconds: number) => void;
}) {
  const author = comment.authorUser?.name ?? comment.authorName ?? 'Guest';

  return (
    <li className="border-b border-hairline py-3 last:border-b-0">
      <div className="flex items-baseline gap-2">
        <span className="text-body font-medium text-chalk">{author}</span>
        <span className="text-caption text-chalk-faint">{timeAgo(comment.createdAt)}</span>
      </div>
      <div className="mt-1 flex items-start gap-2">
        {comment.timecodeSec !== null && comment.timecodeSec !== undefined ? (
          <button
            type="button"
            onClick={() => onSeek(comment.timecodeSec ?? 0)}
            data-timecode
            className="mt-0.5 shrink-0 rounded-xs border border-hairline bg-slate-900 px-1.5 py-0.5 font-mono text-micro text-signal transition-colors duration-120 hover:border-signal/50 hover:bg-signal/10"
            title="Jump to this moment"
          >
            {timecode(comment.timecodeSec)}
          </button>
        ) : null}
        <p className="whitespace-pre-wrap text-body text-chalk-dim">{comment.bodyText}</p>
      </div>
    </li>
  );
}

/** Keeps the "attach the current time" hint honest as the media plays. */
function useCurrentTime(mediaRef: RefObject<HTMLMediaElement | null>, attached: boolean): number {
  const [value, setValue] = useState(0);

  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;
    const update = () => setValue(media.currentTime);
    media.addEventListener('timeupdate', update);
    media.addEventListener('seeked', update);
    return () => {
      media.removeEventListener('timeupdate', update);
      media.removeEventListener('seeked', update);
    };
    // `attached` flips when the player appears — a recording that was still
    // processing when this mounted needs the listeners hooked up afterwards.
  }, [attached, mediaRef]);

  return value;
}

export function Comments({
  shareId,
  mediaRef,
  playerReady = false,
}: {
  shareId: string;
  /**
   * The video or audio element being played. Comments are kind-agnostic: a
   * timecode on a voice note means exactly what it means on a screen share.
   */
  mediaRef: RefObject<HTMLMediaElement | null>;
  /** True once a playable media element exists. */
  playerReady?: boolean;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const [name, setName] = useState(() => viewerName.get());
  const [attachTimecode, setAttachTimecode] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const currentTime = useCurrentTime(mediaRef, playerReady);

  const query = useQuery({
    queryKey: qk.comments(shareId),
    queryFn: () => fetchComments(shareId),
    staleTime: 15_000,
  });

  const mutation = useMutation({
    mutationFn: (input: { bodyText: string; authorName?: string; timecodeSec?: number }) =>
      postComment(shareId, input),
    onSuccess: () => {
      setBody('');
      void queryClient.invalidateQueries({ queryKey: qk.comments(shareId) });
    },
  });

  const seek = (seconds: number) => {
    const media = mediaRef.current;
    if (!media) return;
    media.currentTime = seconds;
    void media.play().catch(() => {
      // Autoplay restrictions; the seek still happened.
    });
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);

    const text = body.trim();
    if (!text) {
      setFormError('Write something first.');
      return;
    }
    if (!user && !name.trim()) {
      setFormError('Add your name so they know who this is from.');
      return;
    }

    if (!user) viewerName.set(name.trim());

    const currentTime = mediaRef.current?.currentTime ?? 0;
    mutation.mutate({
      bodyText: text,
      ...(user ? {} : { authorName: name.trim() }),
      ...(attachTimecode && currentTime > 0 ? { timecodeSec: Math.floor(currentTime) } : {}),
    });
  };

  const comments = query.data ?? [];

  return (
    <section className="space-y-4">
      <h2 className="text-h4 text-chalk">
        Comments{comments.length > 0 ? <span className="ml-2 tnum text-chalk-faint">{comments.length}</span> : null}
      </h2>

      <form onSubmit={submit} className="space-y-3">
        {!user ? (
          <Input
            label="Your name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Who is this from?"
            maxLength={80}
          />
        ) : null}

        <Textarea
          label="Comment"
          rows={3}
          value={body}
          maxLength={4000}
          placeholder="Ask a question, or note the moment you are talking about."
          onChange={(event) => setBody(event.target.value)}
        />

        <label className="flex items-center gap-2 text-caption text-chalk-dim">
          <input
            type="checkbox"
            checked={attachTimecode}
            onChange={(event) => setAttachTimecode(event.target.checked)}
            className="h-3.5 w-3.5 rounded-xs border-hairline bg-slate-900 accent-signal"
          />
          <span>
            Attach the current time{' '}
            <span data-timecode className="font-mono text-chalk-faint">
              {timecode(currentTime)}
            </span>
          </span>
        </label>

        <ErrorText>{formError ?? (mutation.isError ? errorMessage(mutation.error) : null)}</ErrorText>

        <Button type="submit" variant="primary" busy={mutation.isPending}>
          Post comment
        </Button>
      </form>

      {query.isLoading ? <Loading label="Loading comments" /> : null}

      {query.isError ? (
        <p className="text-caption text-chalk-faint">
          Comments could not be loaded right now. {errorMessage(query.error)}
        </p>
      ) : null}

      {!query.isLoading && !query.isError && comments.length === 0 ? (
        <EmptyState title="No comments yet">
          If something here needs a second pair of eyes, say so — with the timecode attached, they
          will land on the exact moment you meant.
        </EmptyState>
      ) : null}

      {comments.length > 0 ? (
        <ul>
          {comments.map((comment) => (
            <CommentBody key={comment.id} comment={comment} onSeek={seek} />
          ))}
        </ul>
      ) : null}
    </section>
  );
}
